import { runInAction, toJS } from 'mobx'
import rootStore from '../..' // Import the root store
import StripeService from '../../../api/modules/api-stripe'
import { wrapWithLoading } from '../../../config/general-actions'
import { loadStripe } from '@stripe/stripe-js'
import { getStripeApiKey } from './config'
import { actSaveUser } from '../../users/actions'

const subscriptionsStore = rootStore.subscriptions
const usersStore = rootStore.users
const { getPlanById } = rootStore

// 'ai-wp-public-chat-shop'
export const stripeCheckoutPlanMetaId = wrapWithLoading(
	async (metaId) => {
		const user = usersStore.requireUserById(usersStore.loggedInUserId)
		const { email, country } = user

		const plans = subscriptionsStore.plans // Get all plans from the store

		const filteredPlans = plans
			.filter((p) => p.product?.metadata?.id)
			.filter(
				(p) =>
					p.product.metadata.id.toLowerCase() === metaId.toLowerCase()
			)
		// Get the first matching plan
		if (filteredPlans.length === 0) {
			throw new Error('No plan found with the given meta ID')
		}
		const plan = filteredPlans[0]
		const meta = plan.product?.metadata || {}
		const priceId = plan.id

		const stripe = await loadStripe(getStripeApiKey(country))

		const response = await StripeService.createCheckoutSession({
			email,
			priceId,
			category: meta.category
		})

		const sessionId = response.id
		const { error } = await stripe.redirectToCheckout({ sessionId })

		if (error) {
			console.error('Error redirecting to checkout', error)
			throw error
		}
	},
	{
		loadingMessage: 'Creating Checkout Session'
	}
)

export const stripeCheckout = wrapWithLoading(
	async (userEmail, priceId, category) => {
		// get logged-in user and get country code
		const user = usersStore.requireUserById(usersStore.loggedInUserId)
		const userCountryCode = user.country

		const stripe = await loadStripe(getStripeApiKey(userCountryCode))

		const response = await StripeService.createCheckoutSession({
			email: userEmail,
			priceId,
			category
		})

		const sessionId = response.id
		const { error } = await stripe.redirectToCheckout({ sessionId })

		if (error) {
			console.error('Error redirecting to checkout', error)
			throw error
		}
	},
	{ loadingMessage: 'Creating Checkout Session' }
)
/**
 * Fetches available subscription plans from Stripe and updates the store.
 * @returns {Promise<void>}
 */
export const stripeLoadPlans = wrapWithLoading(
	async () => {
		const response = await StripeService.getAvailablePlans()
		runInAction(() => {
			subscriptionsStore.setPlans(response['plans'])
		})
	},
	{ loadingMessage: 'Fetching Plans' }
)
/**
 * Fetches the subscription status for a user and updates the user's subscriptions in the store.
 * @param {string} userUid - The unique identifier of the user.
 */
export const stripeLoadUserSubscriptions = wrapWithLoading(
	async (userUid) => {
		try {
			const { loggedInUserId, loggedInUser } = usersStore
			const user = usersStore.requireUserById(userUid)
			if (!user.email) {
				throw new Error(
					JSON.stringify({ status: 'error', action: 'logout' })
				)
			}

			// Response example: { activeSubscriptions: 0, subscriptions: [], activeOneTimePurchases: 0, purchases: [] }
			const response = await StripeService.getSubscriptionStatus(
				user.email // Pass user email
			)

			// Filter out if needed
			const filteredSubscriptions = response.subscriptions
			const filteredPurchases = response.purchases

			runInAction(() => {
				user.setTemporarySubscriptions(filteredSubscriptions)
				user.setTemporaryPurchases(filteredPurchases)

				if (userUid === loggedInUserId) {
					const hasNewSubscriptions = loggedInUser.syncSubscriptions(
						filteredSubscriptions,
						getPlanById
					)

					const hasNewPurchases = loggedInUser.syncPurchases(
						filteredPurchases,
						getPlanById
					)

					if (hasNewSubscriptions || hasNewPurchases) {
						actSaveUser(
							loggedInUser.user_id,
							toJS(loggedInUser.full),
							toJS(loggedInUser.allowed)
						).then(() => {
							// usersStore.saveState() // We already save state in the action
						})
					}
				}
				usersStore.saveState()
			})

			return {
				userUid,
				subscriptions: filteredSubscriptions,
				purchases: filteredPurchases
			}
		} catch (error) {
			console.error('Error fetching subscription status:', error)
			throw error
		}
	},
	{ loadingMessage: 'Checking Subscription Status' }
)

export const stripeCancelSubscription = wrapWithLoading(
	async (userEmail, subId, formValues) => {
		await StripeService.cancelSubscription(userEmail, {
			subId,
			...formValues
		})
		return true
	},
	{ loadingMessage: 'Canceling Subscription' }
)

export const stripeResumeSubscription = wrapWithLoading(
	async (userEmail, subId) => {
		await StripeService.resumeSubscription(userEmail, { subId })
		return true
	},
	{ loadingMessage: 'Resuming Subscription' }
)

export const stripeSuccessHandler = wrapWithLoading(
	async () => {
		return await StripeService.successHandler()
	},
	{ loadingMessage: 'Handling Subscription status' }
)

export const stripeNewPromoCodeForUser = wrapWithLoading(
	async (user) => {
		return await StripeService.newPromoCodeForUser(user)
	},
	{ loadingMessage: 'Creating new promo code' }
)
